import React, { useEffect, useState } from 'react';
import { Root } from '@radix-ui/react-accordion';
import classNames from 'classnames';
import stylesAccordionList from 'features/sport/components/sportNav/sportNavAccordionList/SportNavAccordionList.module.scss';
import { useTranslation } from 'hooks/useLingUI';
import { isMatch, isTruthy } from 'utility/functions';
import { LiveVideoStreamingContainer } from '../liveVideoStreamingContainer';
import { LiveDisciplinaMenuItem } from './components/liveDisciplinaMenuItem/LiveDisciplinaMenuItem';
import { LiveNavFavouriteMenuItem } from './components/liveNavFavouriteMenuItem/LiveNavFavouriteMenuItem';
import { useExpandedItems, useMenu } from './hooks';
import { LIVE_STREAMING_ID, VideoStreamingMode } from '../liveVideoStreaming';
import { useSelector } from 'react-redux';
import { selectIsStreamingOpen, selectStreamingMode } from 'features/settings/selectors';
import useWindowSize from 'hooks/useWindowSize';
import { Icon, IconsEnum } from 'components/Icons';
import styles from './LiveNav.module.scss';
import { useRouter } from 'next/router';
import { LiveScommessaPreMatch } from '../liveScommessaPreMatch';

export type LiveNavProps = { className?: string };

export const LiveNav: React.FC<LiveNavProps> = ({ className }) => {
  const { t } = useTranslation();
  const isStreaming = useSelector(selectIsStreamingOpen);
  const streamingMode = useSelector(selectStreamingMode);
  const expandedItems = useExpandedItems();
  const { ts, discipline } = useMenu({ isTop: false, isLive: true });

  const { isMobile } = useWindowSize();

  const [isMenuOpen, setMenuOpen] = useState(false);

  const { pathname, asPath, isReady } = useRouter();
  const isAvvenimentoPage = isMatch(pathname, 'avvenimento');

  const isModal = isMobile && isMenuOpen;

  useEffect(() => {
    setMenuOpen(false);
  }, [isMobile, asPath, isReady]);

  const renderDiscipline = (): JSX.Element => {
    if (isTruthy(discipline?.length)) {
      return (
        <React.Fragment key={'sidebar'}>
          {discipline
            .filter((disciplina) => disciplina.id !== '-1')
            .filter((disciplina) => !disciplina.isVirtual)
            .map((disciplina) => (
              <LiveDisciplinaMenuItem {...disciplina} key={`disciplina-${disciplina.id}-${ts}`} />
            ))}
        </React.Fragment>
      );
    }

    return <React.Fragment key={'sidebar'} />;
  };

  const renderContent = () => (
    <aside
      className={classNames(styles.sidebar, className ?? '', { [styles.modal]: isModal })}
      suppressHydrationWarning
    >
      <div
        id={`${VideoStreamingMode.minimal}-${LIVE_STREAMING_ID}`}
        className={classNames({ [styles.hidden]: streamingMode !== VideoStreamingMode.minimal })}
      />
      {isStreaming && <LiveVideoStreamingContainer />}
      <div className={classNames(styles.wrapper, className)}>
        <Root type="multiple" className={stylesAccordionList.container} defaultValue={expandedItems}>
          <LiveNavFavouriteMenuItem />
          {renderDiscipline()}
        </Root>
      </div>
    </aside>
  );

  const renderHead = () => {
    let objHeader = (
      <button
        type="button"
        className={classNames(styles.head, { [styles.modal]: isModal, [styles.hidden]: isAvvenimentoPage })}
        onClick={() => setMenuOpen((st) => !st)}
        suppressHydrationWarning
      >
        <span className={classNames(styles.text)}>{t('sport-header-sticky')}</span>
        <Icon
          key={`icon-${isModal}`}
          iconId={isModal ? IconsEnum.CLOSE : IconsEnum.CHEWRON_DOWN}
          color="var(--color-on-bg-secondary)"
        />
      </button>
    );

    return (
      <React.Fragment>
        {objHeader}
        <button
          type="button"
          className={classNames(styles.filters, { [styles.modal]: isModal })}
          suppressHydrationWarning
        >
          <span className={classNames(styles.text)}>{t('sidebar-header')}</span>
        </button>
      </React.Fragment>
    );
  };

  return (
    <div
      className={classNames(styles.container, styles[`${process.env.NEXT_PUBLIC_COUNTRY}`], {
        [styles.modal]: isModal,
      })}
      suppressHydrationWarning
    >
      {renderHead()}
      {renderContent()}
      <LiveScommessaPreMatch className={styles.mobileOff} />
    </div>
  );
};
